import React, { useState, useEffect } from 'react';
import { useAdminEdit } from '../contexts/AdminEditContext';
import { Palette, Check, X, Plus, Trash2 } from 'lucide-react';

const EditableGradient = ({ 
  value, 
  onSave, 
  className = '',
  label = 'Background'
}) => {
  const { isEditMode, isAdmin } = useAdminEdit();
  const [isEditing, setIsEditing] = useState(false);
  const [isGradient, setIsGradient] = useState(true);
  const [colors, setColors] = useState(['#3b82f6', '#1e40af', '#1e3a8a']);
  const [direction, setDirection] = useState('to bottom right');

  useEffect(() => {
    // Parse current value
    if (value && value.includes('gradient')) {
      setIsGradient(true);
      // Extract colors from gradient string
      const colorMatches = value.match(/#[0-9a-fA-F]{6}/g);
      if (colorMatches) {
        setColors(colorMatches);
      }
      // Extract direction
      const dirMatch = value.match(/to\s+[\w\s]+/);
      if (dirMatch) {
        setDirection(dirMatch[0]);
      }
    } else if (value) {
      setIsGradient(false);
      setColors([value]);
    }
  }, [value]);

  if (!isAdmin) {
    return null;
  }

  const handleSave = () => {
    let newValue;
    if (isGradient && colors.length > 0) {
      newValue = `linear-gradient(${direction}, ${colors.join(', ')})`;
    } else {
      newValue = colors[0] || '#3b82f6';
    }
    onSave(newValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    // Reset to original value
    if (value && value.includes('gradient')) {
      setIsGradient(true);
      const colorMatches = value.match(/#[0-9a-fA-F]{6}/g);
      if (colorMatches) {
        setColors(colorMatches);
      }
    } else {
      setIsGradient(false);
      setColors([value || '#3b82f6']);
    }
    setIsEditing(false);
  };

  const addColor = () => {
    setColors([...colors, '#3b82f6']);
  };

  const removeColor = (index) => {
    if (colors.length > 1) {
      setColors(colors.filter((_, i) => i !== index));
    }
  };

  const updateColor = (index, newColor) => {
    const newColors = [...colors];
    newColors[index] = newColor;
    setColors(newColors);
  };

  if (!isEditMode) {
    return null;
  }

  if (isEditing) {
    return (
      <div className="relative z-50">
        <div className="bg-white dark:bg-gray-800 border-2 border-primary-500 rounded-lg shadow-xl p-4 min-w-[320px]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-50">{label}</span>
            <button
              onClick={handleCancel}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {/* Toggle between gradient and solid */}
          <div className="mb-3">
            <div className="flex gap-2">
              <button
                onClick={() => setIsGradient(true)}
                className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                  isGradient
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                Gradient
              </button>
              <button
                onClick={() => setIsGradient(false)}
                className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                  !isGradient
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                Solid
              </button>
            </div>
          </div>

          {isGradient ? (
            <>
              {/* Direction selector */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Direction</label>
                <select
                  value={direction}
                  onChange={(e) => setDirection(e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-50"
                >
                  <option value="to right">To Right</option>
                  <option value="to left">To Left</option>
                  <option value="to bottom">To Bottom</option>
                  <option value="to top">To Top</option>
                  <option value="to bottom right">To Bottom Right</option>
                  <option value="to bottom left">To Bottom Left</option>
                  <option value="to top right">To Top Right</option>
                  <option value="to top left">To Top Left</option>
                </select>
              </div>

              {/* Color stops */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Colors</label>
                <div className="space-y-2">
                  {colors.map((color, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="color"
                        value={color}
                        onChange={(e) => updateColor(index, e.target.value)}
                        className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={color}
                        onChange={(e) => updateColor(index, e.target.value)}
                        className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-50"
                        placeholder="#000000"
                      />
                      {colors.length > 1 && (
                        <button
                          onClick={() => removeColor(index)}
                          className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          title="Remove color"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={addColor}
                    className="w-full px-3 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded text-sm text-gray-600 dark:text-gray-400 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Color
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={colors[0] || '#3b82f6'}
                  onChange={(e) => setColors([e.target.value])}
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={colors[0] || '#3b82f6'}
                  onChange={(e) => setColors([e.target.value])}
                  className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-50"
                  placeholder="#000000"
                />
              </div>
            </div>
          )}

          {/* Preview */}
          <div className="mb-3 p-2 border border-gray-200 dark:border-gray-700 rounded">
            <div
              className="h-12 rounded"
              style={{
                background: isGradient && colors.length > 0
                  ? `linear-gradient(${direction}, ${colors.join(', ')})`
                  : colors[0] || '#3b82f6'
              }}
            />
          </div>

          {/* Save button */}
          <button
            onClick={handleSave}
            className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Check className="w-4 h-4" />
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => setIsEditing(true)}
      className={`${className} p-1.5 bg-primary-600 text-white rounded-full opacity-100 transition-opacity shadow-lg z-10`}
      title={`Edit ${label}`}
    >
      <Palette className="w-3 h-3" />
    </button>
  );
};

export default EditableGradient;
